import { SxProps, Theme } from "@mui/material";
import documentSettingsTokens, {
  documentSettingsResponsiveTokens as r,
} from "./DocumentSettings.tokens";

const {
  grid,
  card,
  header,
  toggle,
  numberRow,
  numberField,
  select,
  primaryButton,
} = documentSettingsTokens;

const selectBase = {
  width: "100%",
  height: { xs: r.select.height, md: select.height },
  borderRadius: select.radius,
  bgcolor: select.bg,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: select.border,
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: select.border,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: select.focusBorder,
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    height: "100%",
    boxSizing: "border-box",
    p: `0 ${select.paddingX}`,
    // Beats MUI's `&&&` reservation for the (larger) default chevron.
    pr: `${select.textPaddingRight} !important`,
    fontSize: { xs: r.select.fontSize, md: select.fontSize },
    fontWeight: select.fontWeight,
    color: select.color,
    lineHeight: "normal",
  },
  // Kept absolutely positioned so MUI's `.MuiSelect-iconOpen` flip survives.
  "& .MuiSelect-icon": {
    right: select.paddingX,
    top: `calc(50% - ${select.iconSize / 2}px)`,
    width: select.iconSize,
    height: select.iconSize,
    pointerEvents: "none",
  },
};

/** Shared geometry of a dropdown row; the two variants below compose it. */
const selectOptionBase = {
  minHeight: select.optionHeight,
  height: select.optionHeight,
  borderRadius: select.optionRadius,
  px: select.optionPaddingX,
  fontSize: select.fontSize,
  fontWeight: select.fontWeight,
  color: select.color,
};

const documentSettingsStyles = {
  /**
   * Two equal columns with `align-items: start` so a short card (Auto
   * Expiration) keeps its natural height instead of stretching to the tall one
   * beside it, exactly as in the design.
   */
  grid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      md: "minmax(0, 1fr) minmax(0, 1fr)",
    },
    gap: grid.gap,
    alignItems: "start",
  } as SxProps<Theme>,

  card: {
    bgcolor: "background.paper",
    border: `1px solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    boxSizing: "border-box",
    p: { xs: r.card.padding, md: card.padding },
    display: "flex",
    flexDirection: "column",
    gap: { xs: r.card.gap, md: card.gap },
  } as SxProps<Theme>,

  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: header.gap,
  } as SxProps<Theme>,

  cardHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
  } as SxProps<Theme>,

  /** Loading variant of `cardHeaderText` — grows so the lines fill the row. */
  skeletonHeaderText: {
    display: "flex",
    flexDirection: "column",
    gap: header.textGap,
    minWidth: 0,
    flex: 1,
  } as SxProps<Theme>,

  cardTitle: {
    color: header.titleColor,
    fontSize: { xs: r.header.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  cardDescription: {
    color: header.descriptionColor,
    fontSize: {
      xs: r.header.descriptionFontSize,
      md: header.descriptionFontSize,
    },
    lineHeight: header.descriptionLineHeight,
  } as SxProps<Theme>,

  toggleWrapper: {
    ml: "auto",
    mt: toggle.marginTop,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: toggle.lockGap,
  } as SxProps<Theme>,

  toggleTrack: (checked: boolean) =>
    ({
      position: "relative",
      flexShrink: 0,
      width: toggle.width,
      height: toggle.height,
      p: 0,
      borderRadius: "999px",
      bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      transition: toggle.transition,
      "&:hover": { bgcolor: checked ? toggle.trackOn : toggle.trackOff },
      "&.Mui-disabled": {
        bgcolor: checked ? toggle.trackOn : toggle.trackOff,
      },
      "&.Mui-focusVisible": {
        outline: "2px solid",
        outlineColor: "primary.main",
        outlineOffset: "2px",
      },
    }) as SxProps<Theme>,

  toggleKnob: (checked: boolean) =>
    ({
      position: "absolute",
      top: toggle.knobTop,
      left: 0,
      width: toggle.knobSize,
      height: toggle.knobSize,
      borderRadius: "50%",
      bgcolor: toggle.knobBg,
      boxShadow: toggle.knobShadow,
      transform: checked ? toggle.knobOffsetOn : toggle.knobOffsetOff,
      transition: toggle.knobTransition,
    }) as SxProps<Theme>,

  toggleLock: {
    fontSize: toggle.lockSize,
    color: toggle.lockColor,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Wrapper around the stacked "… in [ n ] days" rows; dims when disabled. */
  numberRowStack: (enabled: boolean) =>
    ({
      display: "flex",
      flexDirection: "column",
      gap: numberRow.stackGap,
      opacity: enabled ? 1 : numberRow.disabledOpacity,
      transition: numberRow.opacityTransition,
    }) as SxProps<Theme>,

  numberRow: {
    display: "flex",
    alignItems: "center",
    gap: numberRow.gap,
  } as SxProps<Theme>,

  numberRowLabel: {
    // The reference lets the label take the free space; desktop keeps the
    // fixed column so the number boxes line up across the two cards.
    flex: { xs: 1, md: "none" },
    width: { xs: "auto", md: numberRow.labelWidth },
    flexShrink: 0,
    color: numberRow.labelColor,
    fontSize: numberRow.fontSize,
    fontWeight: numberRow.fontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    maxWidth: "none",
    overflow: "visible",
    p: 0,
    m: 0,
  } as SxProps<Theme>,

  numberRowSuffix: {
    width: { xs: r.numberRow.suffixWidth, md: "auto" },
    color: numberRow.suffixColor,
    fontSize: numberRow.fontSize,
    fontWeight: numberRow.suffixFontWeight,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    maxWidth: "none",
    overflow: "visible",
    p: 0,
    m: 0,
  } as SxProps<Theme>,

  /**
   * 64×36 centred box — the design's inline day input. The error message is
   * rendered separately below the row (the box is far too narrow for it), so
   * an invalid value only recolours the border here.
   */
  numberField: (hasError: boolean) =>
    ({
      width: numberField.width,
      flexShrink: 0,
      "& .MuiOutlinedInput-root": {
        height: { xs: r.numberField.height, md: numberField.height },
        p: 0,
        borderRadius: numberField.radius,
        bgcolor: numberField.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: hasError ? "error.main" : numberField.border,
          borderWidth: "1px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: hasError ? "error.main" : numberField.border,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: hasError ? "error.main" : numberField.focusBorder,
          borderWidth: "1px",
        },
        "&.Mui-disabled": {
          bgcolor: numberField.bg,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: numberField.border,
          },
        },
      },
      "& .MuiOutlinedInput-input": {
        height: "100%",
        boxSizing: "border-box",
        p: `0 ${numberField.paddingX}`,
        fontSize: numberField.fontSize,
        fontWeight: numberField.fontWeight,
        lineHeight: "normal",
        color: numberField.color,
        "&.Mui-disabled": {
          WebkitTextFillColor: numberField.color,
          color: numberField.color,
        },
      },
    }) as SxProps<Theme>,

  fieldError: {
    ml: 0,
    mt: 0,
    fontSize: numberRow.errorFontSize,
  } as SxProps<Theme>,

  selectGroup: {
    display: "flex",
    flexDirection: "column",
    gap: select.labelGap,
    // Both small references run the dropdown the full width of the card.
    maxWidth: { xs: "none", md: select.maxWidth },
    width: "100%",
  } as SxProps<Theme>,

  selectLabel: {
    color: select.labelColor,
    fontSize: select.labelFontSize,
    fontWeight: select.labelFontWeight,
    lineHeight: "normal",
    p: 0,
    m: 0,
    maxWidth: "none",
  } as SxProps<Theme>,

  select: {
    ...selectBase,
  } as SxProps<Theme>,

  selectValueText: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  selectError: {
    ...selectBase,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "error.main",
      borderWidth: "1px",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "error.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "error.main",
      borderWidth: "1px",
    },
  } as SxProps<Theme>,

  selectMenu: {
    mt: "6px",
    borderRadius: select.menuRadius,
    border: `1px solid ${select.menuBorder}`,
    boxShadow: select.menuShadow,
    "& .MuiList-root": { p: select.menuPadding },
  } as SxProps<Theme>,

  selectOption: {
    ...selectOptionBase,
    "&:hover": { bgcolor: select.optionHoverBg },
    "&.Mui-selected, &.Mui-selected:hover": {
      bgcolor: select.optionSelectedBg,
    },
  } as SxProps<Theme>,


  selectOptionMultiple: {
    ...selectOptionBase,
    gap: select.optionPaddingX,
    "&:hover, &.Mui-selected:hover": { bgcolor: select.optionHoverBg },
    "&.Mui-selected": {
      bgcolor: "transparent",
      color: select.color,
    },
    "& .MuiCheckbox-root": {
      p: 0,
      color: select.border,
      "&.Mui-checked": { color: select.focusBorder },
    },
  } as SxProps<Theme>,

  buttonRow: (enabled: boolean) =>
    ({
      display: "flex",
      // The action is wrapped in a Tooltip span, so the row has to stretch it
      // for the button's own `width: 100%` to reach the card edges.
      "& > *": { width: { xs: "100%", md: "auto" } },
      opacity: enabled ? 1 : numberRow.disabledOpacity,
      transition: numberRow.opacityTransition,
    }) as SxProps<Theme>,

  primaryButton: {
    // Both small references close each card with a full-width Update.
    width: { xs: "100%", md: "auto" },
    height: { xs: r.primaryButton.height, md: primaryButton.height },
    minWidth: 0,
    px: { xs: r.primaryButton.paddingX, md: primaryButton.paddingX },
    borderRadius: {
      xs: r.primaryButton.radius,
      md: primaryButton.radius,
    },
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    fontSize: { xs: r.primaryButton.fontSize, md: primaryButton.fontSize },
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": { bgcolor: primaryButton.bg, boxShadow: "none" },
    "&.Mui-disabled": {
      bgcolor: primaryButton.disabledBg,
      color: primaryButton.disabledColor,
      cursor: "not-allowed",
      pointerEvents: "auto",
    },
    "& .MuiButton-endIcon": {
      ml: "8px",
      "& > *:first-of-type": { fontSize: primaryButton.lockSize },
    },
  } as SxProps<Theme>,
};

export default documentSettingsStyles;
